import {Autocomplete} from '@mantine/core';
import {type FC, useMemo, useRef, useState} from 'react';
import {IoSearch} from 'react-icons/io5';
import {useGetGroupedSupportCompanies} from '../../../../../../core/repositories/companyRepository';
import {cn} from '../../../../../../lib/classname';
import {CommonLink} from '../../../../../../shared/components/CommonLink';
import {COLUMNS_BY_COMPANY} from '../../routes';

type Props = {
  className?: string;
};

export const CompanySearchInput: FC<Props> = ({className}) => {
  const [searchWord, setSearchWord] = useState('');
  const autocompleteRef = useRef<HTMLInputElement>(null);
  const {data} = useGetGroupedSupportCompanies({queryParams: {}});
  const companiesFromApi = useMemo(
    () =>
      data?.ok
        ? data.value.companyGroups.flatMap((group) => group.companies)
        : [],
    [data],
  );

  const uniqueCompaniesMap = useMemo(() => {
    return companiesFromApi.reduce((acc, company) => {
      if (!acc.has(company.id)) {
        acc.set(company.id, company);
      }
      return acc;
    }, new Map<number, {id: number; name: string; isFollowed: boolean}>());
  }, [companiesFromApi]);

  const companies = useMemo(
    () =>
      Array.from(uniqueCompaniesMap.values()).map((company) => ({
        id: company.id,
        value: company.name,
      })),
    [uniqueCompaniesMap],
  );

  return (
    <div className={className}>
      <Autocomplete
        ref={autocompleteRef}
        type="search"
        icon={<IoSearch className="text-black" />}
        placeholder="企業名で検索"
        data={companies}
        value={searchWord}
        onChange={(value) => setSearchWord(value)}
        itemComponent={(props) => (
          <CommonLink
            key={props.id}
            href={COLUMNS_BY_COMPANY(Number(props.id))}
            className="border-b-2 border-line cursor-pointer flex items-center line-clamp-2 hover:underline text-primary p-4"
            onClick={() => {
              autocompleteRef.current?.blur();
              setSearchWord('');
            }}
          >
            {props.value}
          </CommonLink>
        )}
        limit={1000}
        maxDropdownHeight={320}
        dropdownPosition="bottom"
        classNames={{
          wrapper: 'wrapper w-full',
          input: cn(
            'bg-background rounded-[24px] border-transparent',
            'px-[12px] py-[10px] text-md leading-[24px]',
            'focus:border-primary',
          ),
          rightSection: 'right',
        }}
      />
    </div>
  );
};
